import { useCallback, useState } from 'react';
import { postHboActivation } from 'services/api/hbo/activate';
import { postLinkSessionFinish } from 'services/api/user/linkSessionFinish';
import { useMetrics } from 'hooks/metrics/useMetrics';
import { getRegion } from 'utils/Region';

// Exportadas para que HandlerActivateHbo replique el mismo criterio de reintento (AC 7.7.1)
export const HBO_ACTIVATION_MAX_ATTEMPTS = 3;
export const HBO_ACTIVATION_SUCCESS_STATUS_LIMIT = 400;
const DEFAULT_ERROR_CODE = '500';
const FINISH_DELAY_MS = 3000;

const getRedirectUrl = data => data?.redirect_url || data?.data?.redirect_url || null;

const openActivationPopup = () => {
  if (typeof window === 'undefined' || typeof window.open !== 'function') {
    return null;
  }

  return window.open('', '_blank');
};

const redirectActivationPopup = (popupWindow, redirectUrl) => {
  if (popupWindow && !popupWindow.closed && popupWindow.location) {
    popupWindow.location.assign(redirectUrl);
    return;
  }

  window.open(redirectUrl, '_blank');
};

const closeActivationPopup = popupWindow => {
  if (popupWindow && !popupWindow.closed && typeof popupWindow.close === 'function') {
    popupWindow.close();
  }
};

// AC 7.7.1: hasta 3 intentos ante 4xx/5xx antes de exponer el error al llamador
export const useHboActivate = () => {
  const [hasError, setHasError] = useState(false);
  const [errorCode, setErrorCode] = useState(DEFAULT_ERROR_CODE);
  const { buildMetricsEvent, sendMetricsEvent } = useMetrics();

  const activate = useCallback(async (metricValues = {}, activationParams = {}) => {
    setHasError(false);
    setErrorCode(DEFAULT_ERROR_CODE);
    let lastErrorCode = DEFAULT_ERROR_CODE;
    const shouldSkipMetricDispatch = Boolean(activationParams?.skipMetricDispatch);
    activationParams = { ...activationParams, region: getRegion() };
    const activationPopup = openActivationPopup();

    for (let attempt = 1; attempt <= HBO_ACTIVATION_MAX_ATTEMPTS; attempt += 1) {
      // Reintento secuencial: cada intento depende del resultado del anterior
      // eslint-disable-next-line no-await-in-loop
      const { data, httpStatus } = await postHboActivation(activationParams);
      const redirectUrl = getRedirectUrl(data);

      if (httpStatus < HBO_ACTIVATION_SUCCESS_STATUS_LIMIT && redirectUrl) {
        const linkedToken = activationParams?.linkedToken;
        if (!shouldSkipMetricDispatch) {
          sendMetricsEvent(buildMetricsEvent('select_content', metricValues));
        }
        redirectActivationPopup(activationPopup, redirectUrl);

        if (linkedToken) {
          setTimeout(() => {
            postLinkSessionFinish(linkedToken, activationParams?.code);
          }, FINISH_DELAY_MS);
        }

        return true;
      }

      lastErrorCode = `${httpStatus || DEFAULT_ERROR_CODE}`;
    }

    closeActivationPopup(activationPopup);
    setErrorCode(lastErrorCode);
    setHasError(true);
    return false;
  }, [buildMetricsEvent, sendMetricsEvent]);

  const clearError = useCallback(() => {
    setHasError(false);
    setErrorCode(DEFAULT_ERROR_CODE);
  }, []);

  return { activate, hasError, clearError, errorCode };
};
